import type { ElementType, ErrorInfo, PropsWithChildren, ReactNode } from 'react'
import { PureComponent } from 'react'
import { useTranslation } from 'react-i18next'

import { Blaze, Box, Button } from '@dhl-ui/components'
import { CarPartsIllustration } from '@dhl-ui/illustrations'

import { getConfig } from '~/config'
import { OnboardingMainPage } from './OnboardingMainPage.js'

type Props = {
  PageComponent?: ElementType
}

const ErrorBoundaryPage = ({ PageComponent }: Props) => {
  const { t } = useTranslation()
  const { basePath } = getConfig()

  const handleOnClick = () => {
    window.location.href = basePath
  }

  const Container = PageComponent || OnboardingMainPage

  return (
    <Container isReloadable={false} isLoading={false}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 60px)', // 60px is the height of the header
          width: '100%',
        }}
      >
        <Blaze
          slotProps={{ illustration: { variant: CarPartsIllustration } }}
          title={t('components.errorBoundary.title')}
          description={t('components.errorBoundary.description')}
          sx={{ mt: -15 }}
        >
          <Button color="primary" variant="contained" onClick={handleOnClick}>
            {t('components.errorBoundary.buttonLabel')}
          </Button>
        </Blaze>
      </Box>
    </Container>
  )
}

type ErrorBoundaryProps = PropsWithChildren<
  Props & {
    onError?: (error: Error, errorInfo: ErrorInfo) => void
  }
>

type State = {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends PureComponent<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError } = this.props

    onError?.(error, errorInfo)
  }

  render(): ReactNode {
    const { children, ...rest } = this.props
    const { hasError } = this.state

    if (hasError) {
      return <ErrorBoundaryPage data-testid="error-boundary" {...rest} />
    }
    return children
  }
}
